import { apiClient, ApiError } from '@/lib/api'
import type {
  Consignment,
  ConsignmentTermsBody,
  ConsignmentWriteBody,
  ConsignmentStatus,
  RecordSaleBody,
} from '@/types/consignment'

export interface ConsignmentFilters {
  status?: ConsignmentStatus
  q?: string
  boat_id?: string
  consignor_id?: string
}

export const fetchConsignments = async (
  filters: ConsignmentFilters = {},
): Promise<Consignment[]> => {
  const params: Record<string, string> = {}
  if (filters.status) params.status = filters.status
  if (filters.q) params.q = filters.q
  if (filters.boat_id) params.boat_id = filters.boat_id
  if (filters.consignor_id) params.consignor_id = filters.consignor_id
  return apiClient<Consignment[]>('/inventory/consignments', {
    params: Object.keys(params).length > 0 ? params : undefined,
  })
}

export const fetchConsignmentById = async (id: string): Promise<Consignment> =>
  apiClient<Consignment>(`/inventory/consignments/${id}`)

// 404 means "no open agreement" — translate to null so the boat detail page
// can render an empty state without surfacing an error (same pattern as
// fetchSlipForBoat).
export const fetchConsignmentByBoat = async (boatId: string): Promise<Consignment | null> => {
  try {
    return await apiClient<Consignment>(`/inventory/consignments/by-boat/${boatId}`)
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null
    throw err
  }
}

export const createConsignment = async (body: ConsignmentWriteBody): Promise<Consignment> =>
  apiClient<Consignment>('/inventory/consignments', { method: 'POST', body })

export const updateConsignment = async (
  id: string,
  body: ConsignmentTermsBody,
): Promise<Consignment> =>
  apiClient<Consignment>(`/inventory/consignments/${id}`, { method: 'PUT', body })

export const deleteConsignment = async (id: string): Promise<void> =>
  apiClient<void>(`/inventory/consignments/${id}`, { method: 'DELETE' })

export const recordSale = async (id: string, body: RecordSaleBody): Promise<Consignment> =>
  apiClient<Consignment>(`/inventory/consignments/${id}/sale`, { method: 'POST', body })

export const recordPayout = async (id: string, paidOutAt?: string): Promise<Consignment> =>
  apiClient<Consignment>(`/inventory/consignments/${id}/payout`, {
    method: 'POST',
    body: paidOutAt ? { paid_out_at: paidOutAt } : {},
  })

export const withdrawConsignment = async (id: string): Promise<Consignment> =>
  apiClient<Consignment>(`/inventory/consignments/${id}/withdraw`, { method: 'POST', body: {} })
